<template>
    <Spinner v-if="!isDoneLoading && !isGeneratingBpmn" />
    <Page class="page-container">
        <template #title>
            <Title :level="1" class="page-title">
                <i class="pi pi-share-alt" />
                {{ processName }}
            </Title>
        </template>
        <template #controls>
            <LastChangedInfo
                v-if="process?.identity"
                :entityId="process.identity"
                :entityType="EntityType.PROCESS"
                attribute="process_definition"
            />
            <div
                id="properties-panel"
                ref="propertiesPanelElement"
                :class="{
                    'visible': definition?.content && isEditing,
                }"
            />
            <Button
                v-for="button in buttons"
                :key="button.label"
                :class="button.class"
                :icon="button.icon"
                :label="button.label"
                @click="button.onClick"
            />
            <template v-if="definition?.content"> </template>
        </template>
        <Section
            editable
            class="definition"
            v-if="isDoneLoading || isGeneratingBpmn"
        >
            <template v-if="isViewingHistory">
                <template v-if="comparisonArtefact?.content">
                    <ProcessDefinitionDiff
                        v-if="selectedArtefact?.content"
                        :from="comparisonArtefact.content"
                        :to="selectedArtefact.content"
                        ref="historicalDefinitionElement"
                        :showAsDiff="route.query.compare !== undefined"
                    />
                </template>
                <!--
                    When there are no older artefacts, we can't show a meaningful
                    diff yet.
                -->
                <template v-else>
                    <ProcessDefinition
                        v-if="selectedArtefact?.content"
                        v-model:bpmnArtefact="selectedArtefact as BpmnArtefact"
                        :isMaximizingEnabled="false"
                        ref="historicalDefinitionElement"
                    />
                </template>
            </template>
            <template v-else>
                <div class="process-container" :class="{ 'edit': isEditing }">
                    <ProcessDefinition
                        v-if="definition?.content && !isGeneratingBpmn"
                        v-model:bpmnArtefact="definition"
                        :isMaximizingEnabled="false"
                        ref="definitionElement"
                    >
                        <template #floating-extra>
                            <ButtonGroup>
                                <Button
                                    v-if="organization && stek && process"
                                    severity="secondary"
                                    class="minimize-button"
                                    icon="pi pi-arrow-down-left-and-arrow-up-right-to-center"
                                    label="Minimize"
                                    size="small"
                                    :as="'RouterLink'"
                                    :to="{
                                        name: 'processPage',
                                        params: {
                                            orgName: organization.name,
                                            stekName: stek.name,
                                            processId: process.identity,
                                        },
                                    }"
                                />
                                <Button
                                    severity="secondary"
                                    size="small"
                                    icon="pi pi-ellipsis-v"
                                    aria-haspopup="true"
                                    aria-controls="process_popup_menu"
                                    aria-label="Document Menu"
                                    @click="toggleMenu"
                                />
                            </ButtonGroup>
                            <Menu
                                id="process_popup_menu"
                                ref="menu"
                                :model="contextMenuItems"
                                :popup="true"
                            />
                            <input
                                name="file"
                                type="file"
                                accept=".bpmn"
                                style="display: none"
                                ref="fileUploadElement"
                                @change="
                                    (e) =>
                                        uploadProcessDefinition(
                                            e as HTMLInputEvent
                                        )
                                "
                            />
                        </template>
                    </ProcessDefinition>
                    <ProcessDefinitionCreator
                        v-if="!definition?.content || isGeneratingBpmn"
                        :isEditing="isEditing"
                        v-model="definition"
                    />
                </div>
            </template>
        </Section>
    </Page>
</template>

<script setup lang="ts">
import Button, { ButtonProps } from "primevue/button";
import Menu from "primevue/menu";
import type { MenuItem } from "primevue/menuitem";
import { useConfirm } from "primevue/useconfirm";
import { computed, ref } from "vue";
import { onBeforeRouteLeave, useRoute, useRouter } from "vue-router";

import ProcessDefinitionDiff from "@/components/Diff/ProcessDefinitionDiff.vue";
import LastChangedInfo from "@/components/LastChangedInfo.vue";
import Page from "@/components/Page.vue";
import ProcessDefinition from "@/components/Process/Definition.vue";
import ProcessDefinitionCreator from "@/components/Process/DefinitionCreator.vue";
import Section from "@/components/Section.vue";
import Title from "@/components/Title.vue";
import { useEditManager, useEditStore } from "@/composables/edit-manager";
import { useGenerateBpmnDialog } from "@/composables/generate-bpmn-dialog";
import { useOrganizationManager } from "@/composables/organization-manager";
import {
    useProcessManager,
    useProcessStore,
} from "@/composables/process-manager";
import { useStekManager } from "@/composables/stek-manager";
import { BpmnArtefact } from "@/lib/artefacts";
import { useArtefactsStore } from "@/lib/artefacts/user";
import Spinner from "@/shared/Spinner.vue";
import { EntityType } from "@/stores/entity-store";
import { useHistoryManager, useHistoryStore } from "@/stores/history";
import { useTaskManager } from "@/stores/task-store";
import { HTMLInputEvent } from "@/types";
import { downloadBlob } from "@/utils";

const menu = ref<InstanceType<typeof Menu>>();
const propertiesPanelElement = ref<HTMLElement>();
const { organization } = useOrganizationManager();
const { stek } = useStekManager();
const { updateProcess } = useProcessStore();
const { process, definition, isFetching, isGeneratingBpmn } =
    useProcessManager();
const artefactsStore = useArtefactsStore();

const { startEditing } = useEditStore();
const { isEditing } = useEditManager();
const isRestoring = ref(false);
const router = useRouter();
const route = useRoute();

const { fetchHistory } = useHistoryStore();
const {
    isViewingLatestVersion,
    isViewingHistory,
    selectedArtefact: selectedHistoricalArtefact,
    comparisonArtefact,
    isFetchingHistory,
    isFetchingHistoricalArtefact,
} = useHistoryManager();
const { open: openGenerateDialog } = useGenerateBpmnDialog({
    artefact: definition,
});

const selectedArtefact = computed(() => {
    if (isViewingHistory.value) {
        return selectedHistoricalArtefact.value;
    }
    return definition.value;
});

const fileUploadElement = ref<HTMLElement>();

/**
 * Check if the given filelist contains exactly one BPMN file.
 */
const isBpmnFile = (files?: FileList | null) => {
    if (!files || files.length !== 1) {
        return false;
    }
    return files[0].name.endsWith(".bpmn");
};

const uploadProcessDefinition = async (event: HTMLInputEvent) => {
    if (!event.target.files || !isBpmnFile(event.target.files)) {
        return;
    }
    await definition.value?.updateContent(event.target.files[0]);
    await definition.value?.persist();
    startEditing();
};

const isDoneLoading = computed(() => {
    return (
        !isFetching.value &&
        !isFetchingHistory.value &&
        !isFetchingHistoricalArtefact.value &&
        !isRestoring.value
    );
});

useTaskManager();

const toggleMenu = (event: MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    menu.value?.toggle(event);
};

const confirm = useConfirm();

const processName = computed(() => {
    if (!process.value) {
        return "...";
    }

    return process.value.name;
});

const downloadAsBpmn = () => {
    if (process.value && selectedArtefact.value) {
        downloadBlob(
            URL.createObjectURL(selectedArtefact.value?.blob),
            `${process.value?.name}.bpmn`
        );
    }
};

const definitionElement = ref<InstanceType<typeof ProcessDefinition>>();
const historicalDefinitionElement =
    ref<InstanceType<typeof ProcessDefinition>>();
const selectedDefinitionElement = computed(() => {
    return isViewingHistory.value
        ? historicalDefinitionElement.value
        : definitionElement.value;
});

const downloadAsSvg = async () => {
    if (!selectedDefinitionElement.value || !process.value) {
        return;
    }
    const svg = await selectedDefinitionElement.value.getImage("svg");
    if (!svg) {
        return;
    }

    downloadBlob(URL.createObjectURL(svg), `${process.value.name}.svg`);
};

const downloadAsPng = async () => {
    if (!selectedDefinitionElement.value || !process.value) {
        return;
    }

    const png = await selectedDefinitionElement.value.getImage("png");
    if (!png) {
        return;
    }

    downloadBlob(URL.createObjectURL(png), `${process.value.name}.png`);
};

onBeforeRouteLeave(async () => {
    await definitionElement.value?.completeActiveActions();
});

const restoreConfirmation = async () => {
    return new Promise((resolve) => {
        confirm.require({
            message: "Are you sure you want restore this version?",
            header: "Restore version",
            icon: "pi pi-exclamation-triangle",
            rejectProps: {
                label: "Cancel",
                severity: "secondary",
                outlined: true,
            },
            acceptProps: {
                label: "Restore",
            },
            accept: () => {
                resolve(true);
            },
            reject: () => {
                resolve(false);
            },
            onHide: () => {
                resolve(false);
            },
        });
    });
};

const buttonsAndTheirConditions = computed(() => [
    {
        class: "p-button-secondary",
        icon: "pi pi-check",
        label: "Restore",
        click: async () => {
            const shouldRestore = await restoreConfirmation();
            if (!shouldRestore) {
                return;
            }

            isRestoring.value = true;
            try {
                await artefactsStore.restoreArtefact(selectedArtefact.value!);
                await Promise.all([updateProcess(true), fetchHistory()]);
                await router.push({
                    name: route.name,
                    params: {
                        ...route.params,
                        version: "current",
                    },
                    query: route.query,
                });
            } finally {
                isRestoring.value = false;
            }
        },
        condition: () => !isViewingLatestVersion.value,
    },
]);

const buttons = computed<ButtonProps[]>(() => {
    return buttonsAndTheirConditions.value
        .filter((button) => !button.condition || button.condition())
        .map((button) => {
            return {
                class: button.class,
                icon: button.icon,
                label: button.label,
                onClick: button.click,
            };
        });
});

const contextMenuItemsAndTheirConditions = computed(() => [
    {
        label: "Download as SVG",
        icon: "pi pi-image",
        command: downloadAsSvg,
    },
    {
        label: "Download as PNG",
        icon: "pi pi-image",
        command: downloadAsPng,
    },
    {
        label: "Download as BPMN",
        icon: "pi pi-download",
        command: downloadAsBpmn,
    },
    {
        label: "Upload a BPMN diagram",
        icon: "pi pi-upload",
        command: () => fileUploadElement.value?.click(),
        condition: () => isViewingLatestVersion.value,
    },
    {
        label: "Regenerate process",
        icon: "pi pi-sparkles",
        command: () => openGenerateDialog(),
        condition: () => isViewingLatestVersion.value,
    },
    {
        label: "History",
        icon: "pi pi-history",
        command: () => {
            router.push({
                name: route.name,
                params: {
                    ...route.params,
                    version: "current",
                },
            });
        },
        condition: () => !isViewingHistory.value,
    },
]);

const contextMenuItems = computed<MenuItem[]>(() => {
    return contextMenuItemsAndTheirConditions.value
        .filter((item) => !item.condition || item.condition())
        .map((item) => {
            return {
                label: item.label,
                icon: item.icon,
                command: item.command,
            };
        });
});
</script>

<style scoped lang="scss">
.page-container {
    margin-bottom: calc(var(--page-margin) * -0.5);
}

.page-title {
    display: flex;
    align-items: center;
    gap: 6px;

    i {
        font-size: 1.6rem;
    }
}

.definition {
    flex: 1;
    display: flex;
    padding: 0;
    margin: auto calc(var(--page-margin) * -1);
}

.process-container {
    flex: 1;
    width: 100%;
    display: flex;
    overflow: hidden;
    position: relative;
}
</style>
